import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { Plus, Trash2, FileEdit, ChevronDown, Settings, Download } from 'lucide-react';
import CircleForm from './CircleForm';

const Avatar = ({ employee, size = 36, onClick, title }) => {
  const initial = employee?.name?.charAt(0) || '?';
  const picture =
    employee?.picture ||
    employee?.google_picture ||
    employee?.google_picture_url ||
    employee?.photo_url ||
    employee?.avatar ||
    employee?.image;
  const label = title || employee?.name || 'Employe';
  const className = `rounded-full bg-indigo-500 text-white flex items-center justify-center overflow-hidden border-0 p-0 ${
    onClick ? 'cursor-pointer hover:ring-2 hover:ring-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-300' : ''
  }`;
  const content = picture ? (
    <img src={picture} alt={label} className="w-full h-full object-cover" />
  ) : (
    <span className="font-semibold text-sm">{initial}</span>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        style={{ width: size, height: size }}
        title={label}
        aria-label={label}
      >
        {content}
      </button>
    );
  }
  return (
    <div className={className} style={{ width: size, height: size }} title={label}>
      {content}
    </div>
  );
};

const mentionStyles = {
  circle: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  role: 'bg-amber-50 text-amber-700 border-amber-100',
  project: 'bg-emerald-50 text-emerald-700 border-emerald-100',
};

const mentionTextStyles = {
  circle: 'text-indigo-600 bg-indigo-50/70',
  role: 'text-amber-600 bg-amber-50/70',
  project: 'text-emerald-600 bg-emerald-50/70',
};

const pastelPalette = [
  '#A7C7E7',
  '#BDE0FE',
  '#CDB4DB',
  '#F9C6C9',
  '#FFE5B4',
  '#F7D9C4',
  '#B7E4C7',
  '#C3E5AE',
  '#D4F1F4',
  '#FDE2E4',
];

const hashString = (value) => {
  const str = String(value || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const circleColorPalette = [
  { value: "#A7C7E7", label: "Bleu" },
  { value: "#BDE0FE", label: "Ciel" },
  { value: "#CDB4DB", label: "Lavande" },
  { value: "#F9C6C9", label: "Rose" },
  { value: "#FFE5B4", label: "Peche" },
  { value: "#F7D9C4", label: "Abricot" },
  { value: "#B7E4C7", label: "Menthe" },
  { value: "#C3E5AE", label: "Sauge" },
  { value: "#D4F1F4", label: "Aqua" },
  { value: "#FDE2E4", label: "Blush" },
];

const mentionPattern = /@\[(circle|role|project):(\d+)\|([^\]]+)\]/g;

const parseFocusText = (text) => {
  if (!text) return [];
  const parts = [];
  let lastIndex = 0;
  text.replace(mentionPattern, (match, type, id, label, offset) => {
    if (offset > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, offset) });
    }
    parts.push({ type: 'mention', mentionType: type, id: Number(id), label });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return parts;
};

const buildFocusSegments = (rawText) => {
  if (!rawText) return [];
  const segments = [];
  let lastIndex = 0;
  rawText.replace(mentionPattern, (match, type, id, label, offset) => {
    if (offset > lastIndex) {
      const text = rawText.slice(lastIndex, offset);
      segments.push({ type: 'text', raw: text, display: text });
    }
    const display = `@${label}`;
    segments.push({ type: 'mention', raw: match, display, mentionType: type, id: Number(id), label });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < rawText.length) {
    const text = rawText.slice(lastIndex);
    segments.push({ type: 'text', raw: text, display: text });
  }
  return segments;
};

const segmentsToDisplay = (segments) => segments.map((seg) => seg.display).join('');

const segmentsToRaw = (segments) => segments.map((seg) => seg.raw).join('');

const splitSegmentsAt = (segments, index) => {
  if (index <= 0) return { left: [], right: [...segments] };
  let remaining = index;
  const left = [];
  let right = [];

  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const length = segment.display.length;
    if (remaining > length) {
      left.push(segment);
      remaining -= length;
      continue;
    }
    if (remaining === length) {
      left.push(segment);
      right = segments.slice(i + 1);
      return { left, right };
    }
    if (remaining === 0) {
      right = segments.slice(i);
      return { left, right };
    }
    const before = segment.display.slice(0, remaining);
    const after = segment.display.slice(remaining);
    if (before) {
      left.push({ type: 'text', raw: before, display: before });
    }
    if (after) {
      right = [{ type: 'text', raw: after, display: after }, ...segments.slice(i + 1)];
    } else {
      right = segments.slice(i + 1);
    }
    return { left, right };
  }
  return { left, right };
};

const RoleFocusInput = ({ value, onSave, disabled, mentionItems, placeholder, getMentionStyle }) => {
  const [rawDraft, setRawDraft] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [activeQuery, setActiveQuery] = useState(null);
  const inputRef = useRef(null);
  const overlayRef = useRef(null);
  const ignoreBlurRef = useRef(false);

  useEffect(() => {
    setRawDraft(value || '');
  }, [value]);

  const segments = useMemo(() => buildFocusSegments(rawDraft), [rawDraft]);
  const displayDraft = useMemo(() => segmentsToDisplay(segments), [segments]);

  const updateSuggestions = (text, caretPos) => {
    const before = text.slice(0, caretPos);
    const match = before.match(/@([^\s,]*)$/);
    if (!match) {
      setSuggestions([]);
      setActiveQuery(null);
      return;
    }
    const query = match[1] || '';
    if (query.startsWith('[')) {
      setSuggestions([]);
      setActiveQuery(null);
      return;
    }
    const start = caretPos - match[0].length;
    const filtered = (mentionItems || []).filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    );
    setActiveQuery({ start, end: caretPos, query });
    setSuggestions(filtered.slice(0, 6));
  };

  const applyDisplayChange = (nextDisplay, caretPos) => {
    const prevDisplay = displayDraft;
    if (nextDisplay === prevDisplay) return;
    let start = 0;
    while (
      start < prevDisplay.length &&
      start < nextDisplay.length &&
      prevDisplay[start] === nextDisplay[start]
    ) {
      start += 1;
    }
    let endPrev = prevDisplay.length - 1;
    let endNext = nextDisplay.length - 1;
    while (
      endPrev >= start &&
      endNext >= start &&
      prevDisplay[endPrev] === nextDisplay[endNext]
    ) {
      endPrev -= 1;
      endNext -= 1;
    }
    const removedCount = Math.max(0, endPrev - start + 1);
    const inserted = nextDisplay.slice(start, endNext + 1);

    const { left, right: rest } = splitSegmentsAt(segments, start);
    const { right } = splitSegmentsAt(rest, removedCount);
    const nextSegments = [...left];
    if (inserted) {
      nextSegments.push({ type: 'text', raw: inserted, display: inserted });
    }
    nextSegments.push(...right);
    setRawDraft(segmentsToRaw(nextSegments));
    updateSuggestions(nextDisplay, caretPos);
  };

  const handleChange = (event) => {
    const nextDisplay = event.target.value;
    if (disabled) return;
    const caretPos = event.target.selectionStart ?? nextDisplay.length;
    applyDisplayChange(nextDisplay, caretPos);
  };

  const handleScroll = (event) => {
    if (!overlayRef.current) return;
    overlayRef.current.scrollLeft = event.target.scrollLeft;
  };

  const applyMention = (item) => {
    if (!activeQuery) return;
    const safeLabel = (item.name || '').replace(/\|/g, '').replace(/\]/g, '');
    const token = `@[${item.type}:${item.id}|${safeLabel}]`;
    const displayToken = `@${safeLabel}`;
    const { left, right: rest } = splitSegmentsAt(segments, activeQuery.start);
    const { right } = splitSegmentsAt(rest, activeQuery.end - activeQuery.start);
    const nextSegments = [...left, { type: 'mention', raw: token, display: displayToken }, ...right];
    setRawDraft(segmentsToRaw(nextSegments));
    setSuggestions([]);
    setActiveQuery(null);
    requestAnimationFrame(() => {
      const pos = segmentsToDisplay(left).length + displayToken.length;
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleBlur = () => {
    if (ignoreBlurRef.current) return;
    setSuggestions([]);
    setActiveQuery(null);
    onSave && onSave(rawDraft);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setSuggestions([]);
      setActiveQuery(null);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (suggestions.length > 0 && activeQuery) {
        applyMention(suggestions[0]);
        return;
      }
      onSave && onSave(rawDraft);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="relative w-full">
      <div
        ref={overlayRef}
        className={`absolute inset-0 px-3 py-2 text-sm rounded-lg pointer-events-none overflow-hidden whitespace-pre ${
          disabled ? 'text-slate-500' : 'text-slate-700'
        }`}
      >
        {displayDraft ? (
          segments.map((segment, index) => {
            if (segment.type === 'mention') {
              const mentionClass = mentionTextStyles[segment.mentionType] || 'text-slate-600 bg-slate-100';
              const mentionStyle = getMentionStyle ? getMentionStyle(segment.mentionType, segment.id) : null;
              return (
                <span
                  key={`seg-${index}`}
                  className={`font-semibold px-1 rounded ${mentionClass}`}
                  style={mentionStyle || undefined}
                >
                  {segment.display}
                </span>
              );
            }
            return <span key={`seg-${index}`}>{segment.display}</span>;
          })
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
      </div>
      <input
        ref={inputRef}
        value={displayDraft}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onScroll={handleScroll}
        disabled={disabled}
        placeholder=""
        className={`w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none bg-transparent text-transparent caret-slate-800 ${
          disabled ? 'bg-slate-50 cursor-not-allowed' : ''
        }`}
      />
      {suggestions.length > 0 && !disabled && (
        <div className="absolute z-20 mt-2 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
          {suggestions.map((item) => (
            <button
              key={`${item.type}-${item.id}`}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                ignoreBlurRef.current = true;
                applyMention(item);
                setTimeout(() => {
                  ignoreBlurRef.current = false;
                }, 0);
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
            >
              <span className={`px-2 py-0.5 rounded-full text-[10px] border ${mentionStyles[item.type] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                {item.type}
              </span>
              <span className="text-slate-700 truncate">{item.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StructurePanel = ({
  isEditing,
  selectedCircle,
  employees,
  parentId,
  isRole,
  editingField,
  setEditingField,
  inlineDraft,
  onUpdateInlineField,
  onCommitInline,
  onInlineCancel,
  canAddSubCircle,
  canEditLeader,
  canEditStrategy,
  canEditVoteTimeout,
  canEditColor,
  isLeafRole,
  activeEmployees,
  findEmployee,
  onAddSubCircle,
  onStartTension,
  onSave,
  onDelete,
  onCancelEdit,
  onConsolidateRoots,
  onUpdateInlineListItem,
  onRemoveInlineItem,
  onAddInlineItem,
  onUpdateInlineStrategyItem,
  onAddInlineStrategyItem,
  onRemoveInlineStrategyItem,
  onOpenMeeting,
  onJoinMeeting,
  onOpenMeetingHistory,
  meetingLoading,
  openMeetingsByCircle = new Map(),
  isSuperEditor,
  superEditMode,
  onToggleSuperEdit,
  onStartSuperEdit,
  nameRef,
  purposeRef,
  ownerRef,
  domainNewRef,
  accountabilityNewRef,
  strategyNewRef,
  circleMemberIds = [],
  directRoles = [],
  roleFocusMap = {},
  canEditRoleFocus,
  onSaveRoleFocus,
  mentionItems = [],
  circles = [],
  projects = [],
  onSelectCircle,
  selectedEmployeeId,
  onSelectEmployee,
  onClearEmployee,
  onOpenProject,
  projectsInScope = [],
  canViewProjects = true,
  onExportOrganization,
  organizationExporting = false,
  metricsRange,
  metricsRangeValid,
  onMetricsRangeChange,
  currentSlotRange,
  isUsingSlotRange,
  circleMetricsSummary,
  circleMetricsLoading,
  circleMetricsError,
}) => {
  const activeMeeting = selectedCircle?.id ? openMeetingsByCircle.get(String(selectedCircle.id)) : null;
  const canManageMeeting = selectedCircle?.id && !isRole;
  const [meetingMenuOpen, setMeetingMenuOpen] = useState(false);
  const meetingMenuRef = useRef(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsMenuRef = useRef(null);
  const circleById = useMemo(() => {
    const map = new Map();
    (circles || []).forEach((circle) => {
      if (circle?.id) map.set(Number(circle.id), circle);
    });
    return map;
  }, [circles]);
  const projectById = useMemo(() => {
    const map = new Map();
    (projects || []).forEach((project) => {
      if (project?.id) map.set(Number(project.id), project);
    });
    return map;
  }, [projects]);
  const circleColorCache = useMemo(() => new Map(), [circleById]);
  const childCountByParent = useMemo(() => {
    const counts = new Map();
    (circles || []).forEach((circle) => {
      if (circle?.parent_id === null || circle?.parent_id === undefined || circle?.parent_id === '') return;
      const key = String(circle.parent_id);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [circles]);
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const getCircleKey = (circle) => {
    if (!circle) return '';
    if (circle?.id !== undefined && circle?.id !== null) return `id:${circle.id}`;
    if (circle?.name) return `name:${circle.name}`;
    return 'circle';
  };
  const toRgb = (value) => {
    const color = d3.color(value);
    if (!color) return null;
    return { r: color.r, g: color.g, b: color.b };
  };
  const mixColors = (first, second, weight = 0.5) => ({
    r: Math.round(first.r * (1 - weight) + second.r * weight),
    g: Math.round(first.g * (1 - weight) + second.g * weight),
    b: Math.round(first.b * (1 - weight) + second.b * weight),
  });
  const rgbToCss = (rgb) => `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  const rgbaToCss = (rgb, alpha) => `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  const deriveColorFromParent = (parentColor, circle) => {
    const color = d3.hsl(parentColor);
    if (Number.isNaN(color.h)) return parentColor;
    const seed = hashString(getCircleKey(circle));
    const hueShift = (seed % 11) - 5;
    const lightShift = ((seed >> 3) % 8) - 4;
    color.h = (color.h + hueShift + 360) % 360;
    color.s = clamp(color.s + 0.02, 0.25, 0.55);
    color.l = clamp(color.l + lightShift / 100, 0.78, 0.92);
    return color.formatHex();
  };
  const resolveCircleColor = (circleId, visited = new Set()) => {
    if (!circleId) return null;
    const key = String(circleId);
    if (circleColorCache.has(key)) return circleColorCache.get(key);
    if (visited.has(key)) return null;
    visited.add(key);
    const circle = circleById.get(Number(circleId));
    if (!circle) return null;
    let base = circle.color;
    if (base && !d3.color(base)) base = null;
    if (!base) {
      const seedColor = pastelPalette[hashString(getCircleKey(circle)) % pastelPalette.length];
      if (circle.parent_id) {
        const parentColor = resolveCircleColor(circle.parent_id, visited);
        base = parentColor ? deriveColorFromParent(parentColor, circle) : seedColor;
      } else {
        base = seedColor;
      }
    }
    circleColorCache.set(key, base);
    return base;
  };
  const getMentionTagStyle = (mentionType, id) => {
    if (mentionType !== 'circle' && mentionType !== 'role') return null;
    const resolved = resolveCircleColor(id);
    const rgb = toRgb(resolved);
    if (!rgb) return null;
    const text = mixColors(rgb, { r: 0, g: 0, b: 0 }, 0.55);
    return {
      backgroundColor: rgbaToCss(rgb, 0.22),
      borderColor: rgbaToCss(rgb, 0.6),
      color: rgbToCss(text),
    };
  };
  const formatNumber = (value, decimals = 0) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0';
    return num.toLocaleString('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
  const updateMetricsRange = (key, value) => {
    if (!onMetricsRangeChange) return;
    onMetricsRangeChange((prev) => ({ ...prev, [key]: value }));
  };
  const applyCurrentSlotRange = () => {
    if (!onMetricsRangeChange || !currentSlotRange) return;
    onMetricsRangeChange({
      start: currentSlotRange.start,
      end: currentSlotRange.end,
    });
  };
  const metricsSummary = circleMetricsSummary || {
    hours: 0,
    expenses: 0,
    projects: 0,
    covered: 0,
    velocity: 0,
  };
  const ownerTitle = isLeafRole ? 'Affectations du role' : 'Leader';
  const ownerSelectEmptyLabel = 'Pas de leader';
  const ownerExtraLabel = isLeafRole ? 'Affectations' : 'Co-leaders';
  const ownerExtraAddLabel = isLeafRole ? 'Ajouter une personne' : 'Ajouter un co-leader';
  const ownerCardLabel = isLeafRole ? '' : 'Leader';
  const ownerEmptyLabel = isLeafRole ? 'Aucune affectation' : 'Aucun leader';
  const selectedEmployee =
    selectedEmployeeId !== null && selectedEmployeeId !== undefined
      ? findEmployee(selectedEmployeeId)
      : null;
  const employeeRoles = useMemo(() => {
    if (selectedEmployeeId === null || selectedEmployeeId === undefined) return [];
    const idNumber = Number(selectedEmployeeId);
    if (Number.isNaN(idNumber)) return [];
    const isRoleCircle = (circle) =>
      Boolean(circle?.parent_id) && !(childCountByParent.get(String(circle.id)));
    const hasOwner = (circle) => {
      if (!circle) return false;
      const owners = new Set();
      if (circle.owner_id !== null && circle.owner_id !== undefined && circle.owner_id !== '') {
        owners.add(Number(circle.owner_id));
      }
      (circle.owner_ids || []).forEach((ownerId) => {
        if (ownerId !== null && ownerId !== undefined && ownerId !== '') {
          owners.add(Number(ownerId));
        }
      });
      return owners.has(idNumber);
    };
    return (circles || [])
      .filter((circle) => circle?.id && isRoleCircle(circle) && hasOwner(circle))
      .map((role) => ({
        ...role,
        parentName: circleById.get(Number(role.parent_id))?.name || '',
      }))
      .sort((a, b) => {
        const parentCompare = (a.parentName || '').localeCompare(b.parentName || '', 'fr');
        if (parentCompare !== 0) return parentCompare;
        return (a.name || '').localeCompare(b.name || '', 'fr');
      });
  }, [selectedEmployeeId, circles, circleById, childCountByParent]);
  const rawVoteTimeout = Number(
    inlineDraft.vote_timeout_days ?? selectedCircle?.vote_timeout_days ?? 3,
  );
  const voteTimeoutDisplay = Number.isNaN(rawVoteTimeout) || rawVoteTimeout < 1 ? 3 : rawVoteTimeout;
  const roleOwners = useMemo(() => {
    const ids = new Set();
    (inlineDraft.owner_ids || []).forEach((id) => ids.add(id));
    if (inlineDraft.owner_id) ids.add(inlineDraft.owner_id);
    return Array.from(ids);
  }, [inlineDraft.owner_ids, inlineDraft.owner_id]);
  const inProgressProjects = useMemo(
    () =>
      (projectsInScope || []).filter(
        (project) => String(project?.status || '').toLowerCase() === 'in_progress',
      ),
    [projectsInScope],
  );
  const updateRoleOwners = (nextOwners) => {
    const uniqueOwners = Array.from(new Set(nextOwners.filter(Boolean)));
    onUpdateInlineField('owner_ids', uniqueOwners);
    onUpdateInlineField('owner_id', uniqueOwners[0] || '');
  };
  const slotButtonDisabled = !currentSlotRange;
  const slotButtonClassName = isUsingSlotRange
    ? 'text-[11px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 hover:bg-emerald-100'
    : slotButtonDisabled
      ? 'text-[11px] uppercase tracking-wider text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 cursor-not-allowed'
      : 'text-[11px] uppercase tracking-wider text-slate-600 bg-white border border-slate-200 rounded-full px-2.5 py-1 hover:border-emerald-200 hover:text-emerald-700';

  const renderFocusText = (text) => {
    if (!text) {
      return <span className="text-slate-400">Aucun focus</span>;
    }
    const parts = parseFocusText(text);
    return parts.map((part, index) => {
      if (part.type === 'text') {
        return <span key={`text-${index}`}>{part.value}</span>;
      }
      const mentionType = part.mentionType;
      const label =
        mentionType === 'project'
          ? (projectById.get(part.id)?.name || part.label)
          : (circleById.get(part.id)?.name || part.label);
      const clickable = mentionType === 'project' ? Boolean(onOpenProject) : Boolean(onSelectCircle);
      const mentionClass = mentionStyles[mentionType] || 'bg-slate-100 text-slate-600 border-slate-200';
      const mentionStyle = getMentionTagStyle(mentionType, part.id);
      return (
        <button
          type="button"
          key={`mention-${index}`}
          onClick={() => {
            if (mentionType === 'project') {
              onOpenProject && onOpenProject(part.id);
            } else {
              const circle = circleById.get(part.id);
              if (circle && onSelectCircle) onSelectCircle(circle);
            }
          }}
          className={`inline-flex items-center px-2 py-0.5 mx-0.5 rounded-full border text-xs font-medium ${mentionClass} ${
            clickable ? 'hover:opacity-80' : 'cursor-default'
          }`}
          style={mentionStyle || undefined}
          disabled={!clickable}
        >
          @{label}
        </button>
      );
    });
  };

  const renderMentionInline = (text) => {
    if (!text) return null;
    const parts = parseFocusText(text);
    return parts.map((part, index) => {
      if (part.type === 'text') {
        return <span key={`text-${index}`}>{part.value}</span>;
      }
      const mentionType = part.mentionType;
      const label =
        mentionType === 'project'
          ? (projectById.get(part.id)?.name || part.label)
          : (circleById.get(part.id)?.name || part.label);
      const clickable = mentionType === 'project' ? Boolean(onOpenProject) : Boolean(onSelectCircle);
      const mentionClass = mentionStyles[mentionType] || 'bg-slate-100 text-slate-600 border-slate-200';
      const mentionStyle = getMentionTagStyle(mentionType, part.id);
      return (
        <button
          type="button"
          key={`mention-${index}`}
          onClick={() => {
            if (mentionType === 'project') {
              onOpenProject && onOpenProject(part.id);
            } else {
              const circle = circleById.get(part.id);
              if (circle && onSelectCircle) onSelectCircle(circle);
            }
          }}
          className={`inline-flex items-center px-2 py-0.5 mx-0.5 rounded-full border text-xs font-medium ${mentionClass} ${
            clickable ? 'hover:opacity-80' : 'cursor-default'
          }`}
          style={mentionStyle || undefined}
          disabled={!clickable}
        >
          @{label}
        </button>
      );
    });
  };

  const renderLeaderFocus = (circleId, leaderId) => {
    const focusKey = `${circleId}:${leaderId}`;
    const focusValue = roleFocusMap[focusKey] || '';
    if (canEditRoleFocus) {
      return (
        <RoleFocusInput
          value={focusValue}
          onSave={(next) => onSaveRoleFocus && onSaveRoleFocus(circleId, Number(leaderId), next)}
          disabled={!canEditRoleFocus}
          mentionItems={mentionItems}
          placeholder="Ajouter un focus (optionnel)"
          getMentionStyle={getMentionTagStyle}
        />
      );
    }
    return <div className="text-sm text-slate-600">{renderFocusText(focusValue)}</div>;
  };

  useEffect(() => {
    if (activeMeeting) setMeetingMenuOpen(false);
  }, [activeMeeting]);

  useEffect(() => {
    setMeetingMenuOpen(false);
    setSettingsMenuOpen(false);
  }, [selectedCircle?.id]);

  useEffect(() => {
    if (!meetingMenuOpen) return;
    const handleClickOutside = (event) => {
      if (!meetingMenuRef.current) return;
      if (!meetingMenuRef.current.contains(event.target)) {
        setMeetingMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [meetingMenuOpen]);

  useEffect(() => {
    if (!settingsMenuOpen) return;
    const handleClickOutside = (event) => {
      if (!settingsMenuRef.current) return;
      if (!settingsMenuRef.current.contains(event.target)) {
        setSettingsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [settingsMenuOpen]);

  return (
  <>
    {isEditing ? (
      <CircleForm
        circle={selectedCircle}
        employees={employees}
        mentionItems={mentionItems}
        getMentionStyle={getMentionTagStyle}
        onSave={onSave}
        onDelete={onDelete}
        onCancel={onCancelEdit}
        parentId={parentId}
        isRole={isRole}
      />
    ) : (
      <div className="bg-white p-6 rounded-lg shadow-md h-full overflow-y-auto">
        {selectedEmployeeId !== null && selectedEmployeeId !== undefined ? (
          <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Avatar employee={selectedEmployee || { name: `Employe ${selectedEmployeeId}` }} size={48} />
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">
                    {selectedEmployee?.name || `Employe ${selectedEmployeeId}`}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {employeeRoles.length} role{employeeRoles.length > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              {onClearEmployee && (
                <button
                  type="button"
                  onClick={onClearEmployee}
                  className="text-xs font-semibold text-slate-600 border border-slate-200 rounded-full px-3 py-1 hover:bg-slate-50"
                >
                  Retour aux détails du cercle
                </button>
              )}
            </div>
            {employeeRoles.length > 0 ? (
              <div className="space-y-2">
                {employeeRoles.map((role) => (
                  <div
                    key={role.id}
                    className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-slate-200 bg-white p-3"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-800">{role.name}</p>
                      {role.parentName && (
                        <p className="text-xs text-slate-500">Cercle: {role.parentName}</p>
                      )}
                      <div className="text-xs text-slate-600">
                        {(() => {
                          const focusKey = `${role.id}:${selectedEmployeeId}`;
                          const focusValue = roleFocusMap[focusKey] || '';
                          return focusValue ? (
                            <span className="inline-flex flex-wrap gap-1">
                              <span className="font-semibold text-slate-500">Focus:</span>
                              {renderFocusText(focusValue)}
                            </span>
                          ) : (
                            <span className="text-slate-400">Aucun focus</span>
                          );
                        })()}
                      </div>
                    </div>
                    {onSelectCircle && (
                      <button
                        type="button"
                        onClick={() => {
                          onSelectCircle(role);
                          onClearEmployee && onClearEmployee();
                        }}
                        className="text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-full px-3 py-1 hover:bg-indigo-50"
                      >
                        Voir le role
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">Aucun role assigne.</p>
            )}
          </div>
        ) : selectedCircle ? (
          !selectedCircle.id ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center text-amber-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22v-6" /><path d="M5 16l7-7 7 7" /><path d="M12 9V3" /></svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Structure Multiple détectée</h2>
                <p className="text-slate-500 max-w-xs mx-auto">
                  Vous avez plusieurs cercles racines. Ce cercle "Organization" est virtuel et sert à les regrouper.
                </p>
              </div>
              <button
                onClick={onConsolidateRoots}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors font-medium flex items-center"
              >
                <svg className="mr-2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" /></svg>
                Consolider sous un cercle racine
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-start">
                {editingField === 'name' ? (
                  <input
                    ref={nameRef}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-lg font-semibold text-slate-800 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100 sm:flex-1"
                    value={inlineDraft.name}
                    onChange={(e) => onUpdateInlineField('name', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onCommitInline({ ...inlineDraft, name: inlineDraft.name.trim() || 'Sans nom' }, true);
                      if (e.key === 'Escape') onInlineCancel();
                    }}
                    onBlur={() => onCommitInline({ ...inlineDraft, name: inlineDraft.name.trim() || 'Sans nom' }, true)}
                    placeholder="Nom du cercle"
                  />
                ) : (
                  <div className="flex items-center gap-2 min-w-0">
                    <h2 className="text-2xl font-bold text-gray-800 break-words" title="Nom du cercle">
                      {selectedCircle.name}
                    </h2>
                    {activeMeeting && (
                      <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100">
                        Réunion en cours
                      </span>
                    )}
                  </div>
                )}
                <div className="flex gap-2 flex-wrap items-center w-full sm:w-auto sm:justify-end">
                  {isSuperEditor && (
                    <button
                      onClick={onToggleSuperEdit}
                      className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-full border ${
                        superEditMode ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-white text-slate-400 border-slate-200 hover:text-slate-600'
                      }`}
                    >
                      Super edition {superEditMode ? 'ON' : 'OFF'}
                    </button>
                  )}
                  {superEditMode && selectedCircle?.id && (
                    <button
                      onClick={onStartSuperEdit}
                      className="px-3 py-1.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100 rounded-lg hover:bg-amber-100 flex items-center"
                    >
                      <FileEdit size={14} className="mr-1" /> Editer le cercle
                    </button>
                  )}
                  {canManageMeeting && (
                    activeMeeting ? (
                      <button
                        onClick={() => onJoinMeeting && onJoinMeeting(selectedCircle)}
                        className="px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg hover:bg-emerald-100 flex items-center disabled:opacity-60"
                        disabled={meetingLoading}
                      >
                        Rejoindre la réunion
                      </button>
                    ) : (
                      <div className="relative" ref={meetingMenuRef}>
                        <button
                          onClick={() => setMeetingMenuOpen((prev) => !prev)}
                          className="px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg hover:bg-emerald-100 flex items-center gap-1 disabled:opacity-60"
                          disabled={meetingLoading}
                        >
                          Créer une réunion
                          <ChevronDown size={14} />
                        </button>
                        {meetingMenuOpen && (
                          <div className="absolute right-0 mt-2 w-40 rounded-lg border border-slate-200 bg-white shadow-lg z-10">
                            <button
                              onClick={() => {
                                setMeetingMenuOpen(false);
                                onOpenMeeting && onOpenMeeting('gouvernance', selectedCircle);
                              }}
                              className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                            >
                              Gouvernance
                            </button>
                            <button
                              onClick={() => {
                                setMeetingMenuOpen(false);
                                onOpenMeeting && onOpenMeeting('tactique', selectedCircle);
                              }}
                              className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                            >
                              Tactique
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  )}
                  {selectedCircle?.id && onOpenMeetingHistory && (
                    <button
                      onClick={() => onOpenMeetingHistory(selectedCircle)}
                      className="px-3 py-1.5 text-xs font-semibold bg-slate-50 text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-100 flex items-center"
                    >
                      Historique
                    </button>
                  )}
                  {selectedCircle?.id && (
                    <div className="relative" ref={settingsMenuRef}>
                      <button
                        type="button"
                        onClick={() => setSettingsMenuOpen((prev) => !prev)}
                        className={`p-2 rounded-lg border ${
                          settingsMenuOpen
                            ? 'bg-slate-100 text-slate-700 border-slate-200'
                            : 'bg-white text-slate-400 border-slate-200 hover:text-slate-600'
                        }`}
                        title="Parametres du cercle"
                      >
                        <Settings size={16} />
                      </button>
                      {settingsMenuOpen && (
                        <div className="absolute right-0 mt-2 w-64 rounded-lg border border-slate-200 bg-white shadow-lg z-10 p-3 space-y-3">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Parametres</p>
                          <div className="space-y-1">
                            <p className="text-xs text-slate-500">Delai vote auto-oui</p>
                            {canEditVoteTimeout ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  value={inlineDraft.vote_timeout_days ?? ''}
                                  onChange={(e) => onUpdateInlineField('vote_timeout_days', e.target.value)}
                                  onBlur={(e) =>
                                    onCommitInline({ ...inlineDraft, vote_timeout_days: e.target.value }, true)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      onCommitInline({ ...inlineDraft, vote_timeout_days: e.currentTarget.value }, true);
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      onInlineCancel();
                                    }
                                  }}
                                  className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-xs focus:border-indigo-300 focus:outline-none"
                                />
                                <span className="text-xs text-slate-500">jour(s)</span>
                              </div>
                            ) : (
                              <p className="text-xs text-slate-700">
                                {voteTimeoutDisplay} jour(s)
                              </p>
                            )}
                            <p className="text-[11px] text-slate-400">Non-vote = oui</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-slate-500">Couleur du cercle</p>
                            {canEditColor ? (
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => onCommitInline({ ...inlineDraft, color: '' })}
                                  className={`px-2 py-1 text-[11px] rounded-full border ${
                                    inlineDraft.color ? 'border-slate-200 text-slate-500' : 'border-slate-400 text-slate-700'
                                  }`}
                                >
                                  Auto
                                </button>
                                {circleColorPalette.map((option) => {
                                  const isActive = option.value === inlineDraft.color;
                                  return (
                                    <button
                                      key={option.value}
                                      type="button"
                                      onClick={() => onCommitInline({ ...inlineDraft, color: option.value })}
                                      className={`h-6 w-6 rounded-full border ${
                                        isActive ? 'border-slate-600 ring-2 ring-slate-300' : 'border-white'
                                      }`}
                                      style={{ backgroundColor: option.value }}
                                      title={option.label}
                                      aria-label={option.label}
                                    />
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span
                                  className="h-4 w-4 rounded-full border border-slate-200"
                                  style={{ backgroundColor: inlineDraft.color || '#e2e8f0' }}
                                />
                                <span className="text-xs text-slate-600">
                                  {inlineDraft.color || 'Auto'}
                                </span>
                              </div>
                            )}
                          </div>
                          {onExportOrganization && (
                            <button
                              type="button"
                              onClick={() => {
                                setSettingsMenuOpen(false);
                                onExportOrganization();
                              }}
                              disabled={organizationExporting}
                              className="w-full flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                            >
                              <Download size={14} />
                              {organizationExporting ? 'Export en cours...' : 'Exporter JSON'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => onStartTension(selectedCircle)}
                    className="px-3 py-1.5 text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg hover:bg-indigo-100 flex items-center"
                    disabled={!selectedCircle?.id}
                  >
                    <FileEdit size={14} className="mr-1" /> Créer une tension
                  </button>
                  {superEditMode && canAddSubCircle && (
                    <button
                      onClick={onAddSubCircle}
                      className="px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-lg hover:bg-emerald-100 flex items-center"
                    >
                      <Plus size={14} className="mr-1" /> Sous-cercle
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Raison d'etre</h3>
                  {editingField === 'purpose' ? (
                    <textarea
                      ref={purposeRef}
                      value={inlineDraft.purpose}
                      onChange={(e) => onUpdateInlineField('purpose', e.target.value)}
                      rows={3}
                      className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                      placeholder="Non définie"
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') onInlineCancel();
                      }}
                      onBlur={() => onCommitInline(inlineDraft, true)}
                    />
                  ) : (
                    <p className="mt-2 text-gray-800 italic" title="Raison d'etre">
                      {selectedCircle.purpose || 'Non définie'}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                      {ownerTitle}
                    </h3>
                    {canEditLeader && editingField !== 'owner' && (
                      <button onClick={() => setEditingField('owner')} className="text-xs text-indigo-600 hover:text-indigo-800">
                        Modifier
                      </button>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    {editingField === 'owner' ? (
                      <div className="w-full space-y-3">
                        {!isLeafRole && (
                          <select
                            ref={ownerRef}
                            value={inlineDraft.owner_id || ''}
                            onChange={(e) => onUpdateInlineField('owner_id', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none"
                          >
                            <option value="">{ownerSelectEmptyLabel}</option>
                            {activeEmployees.map((emp) => (
                              <option key={emp.id} value={emp.id}>
                                {emp.name}
                              </option>
                            ))}
                          </select>
                        )}

                        {isLeafRole && (
                          <div>
                            <p className="text-xs text-slate-500 mb-1">{ownerExtraLabel}</p>
                            {roleOwners.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {roleOwners.map((id) => {
                                  const emp = findEmployee(id);
                                  return (
                                    <span
                                      key={id}
                                      className="flex items-center gap-2 bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm border border-slate-200"
                                    >
                                      {emp ? emp.name : id}
                                      <button
                                        type="button"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          updateRoleOwners(roleOwners.filter((ownerId) => ownerId !== id));
                                        }}
                                        className="text-slate-400 hover:text-red-500"
                                      >
                                        &#10005;
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-400">Aucune affectation.</p>
                            )}
                            <select
                              value=""
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val) {
                                  updateRoleOwners([...roleOwners, val]);
                                  e.target.value = '';
                                }
                              }}
                              className="mt-2 w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none"
                            >
                              <option value="">{ownerExtraAddLabel}</option>
                              {activeEmployees
                                .filter((emp) => !roleOwners.includes(emp.id))
                                .map((emp) => (
                                  <option key={emp.id} value={emp.id}>
                                    {emp.name}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <button
                            onClick={() => onCommitInline(inlineDraft, true)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                          >
                            Enregistrer
                          </button>
                          <button
                            onClick={onInlineCancel}
                            className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : selectedCircle.owner_ids && selectedCircle.owner_ids.length > 0 ? (
                      <div className="flex flex-wrap gap-3">
                        {selectedCircle.owner_ids.map((id) => {
                          const emp = findEmployee(id);
                          return (
                            <div
                              key={id}
                              className="flex flex-col gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 shadow-sm min-w-[180px]"
                            >
                              <div className="flex items-center gap-2">
                                <Avatar
                                  employee={emp}
                                  size={36}
                                  onClick={
                                    emp?.id !== undefined && emp?.id !== null
                                      ? () => onSelectEmployee && onSelectEmployee(emp.id)
                                      : undefined
                                  }
                                />
                                <div>
                                  <p className="text-sm font-semibold text-slate-800">{emp ? emp.name : id}</p>
                                  {ownerCardLabel ? (
                                    <p className="text-xs text-slate-500">{ownerCardLabel}</p>
                                  ) : null}
                                </div>
                              </div>
                              {isLeafRole && (
                                <div className="pt-1">
                                  {renderLeaderFocus(selectedCircle.id, id)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : selectedCircle.owner_id ? (
                      <div className="flex flex-col gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 shadow-sm min-w-[180px]">
                        <div className="flex items-center gap-2">
                          <Avatar
                            employee={findEmployee(selectedCircle.owner_id)}
                            size={36}
                            onClick={
                              selectedCircle.owner_id !== undefined && selectedCircle.owner_id !== null
                                ? () => onSelectEmployee && onSelectEmployee(selectedCircle.owner_id)
                                : undefined
                            }
                          />
                          <div>
                            <p className="text-sm font-semibold text-slate-800">
                              {findEmployee(selectedCircle.owner_id)?.name || selectedCircle.owner_id}
                            </p>
                            {ownerCardLabel ? (
                              <p className="text-xs text-slate-500">{ownerCardLabel}</p>
                            ) : null}
                          </div>
                        </div>
                        {isLeafRole && (
                          <div className="pt-1">
                            {renderLeaderFocus(selectedCircle.id, selectedCircle.owner_id)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm">{ownerEmptyLabel}</p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Strategie</h3>
                    {canEditStrategy && editingField !== 'strategy' && (
                      <button onClick={() => setEditingField('strategy')} className="text-xs text-indigo-600 hover:text-indigo-800">
                        Modifier
                      </button>
                    )}
                  </div>
                  {editingField === 'strategy' ? (
                    <div className="space-y-2">
                      {(inlineDraft.strategy || []).map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input
                            value={item.content}
                            onChange={(e) => onUpdateInlineStrategyItem(i, e.target.value)}
                            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none"
                            placeholder="Point strategique..."
                            onBlur={() => onCommitInline(inlineDraft)}
                          />
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              onRemoveInlineStrategyItem(i);
                            }}
                            className="text-slate-400 hover:text-red-500"
                          >
                            &#10005;
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          ref={strategyNewRef}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              onAddInlineStrategyItem(e.currentTarget.value);
                              e.currentTarget.value = '';
                            }
                            if (e.key === 'Escape') onInlineCancel();
                          }}
                          onBlur={() => onCommitInline(inlineDraft, true)}
                          className="flex-1 rounded-lg border border-dashed border-amber-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                          placeholder="Ajouter un point strategique (Entree)"
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const input = e.currentTarget.previousSibling;
                            if (input && input.value) {
                              onAddInlineStrategyItem(input.value);
                              input.value = '';
                            }
                          }}
                          className="px-3 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                        >
                          Ajouter
                        </button>
                      </div>
                    </div>
                  ) : inlineDraft.strategy && inlineDraft.strategy.length > 0 ? (
                    <ul className="space-y-2" title="Strategie">
                      {inlineDraft.strategy.map((item, i) => (
                        <li key={i} className="group relative flex items-start gap-2 text-gray-700">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>
                          <span>{item.content}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 text-sm" title="Strategie">
                      Aucune strategie définie.
                    </p>
                  )}
                </div>

                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Redevabilites</h3>
                    </div>
                    {selectedCircle.accountabilities && selectedCircle.accountabilities.length > 0 ? (
                      <ul className="space-y-2" title="Redevabilites">
                        {selectedCircle.accountabilities.map((acc, i) => (
                          <li key={i} className="group relative flex items-start gap-2 text-gray-700">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></span>
                            <span>{renderMentionInline(acc)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-400 text-sm">Aucune redevabilite définie.</p>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Domaines</h3>
                    </div>
                    {selectedCircle.domains && selectedCircle.domains.length > 0 ? (
                      <ul className="space-y-2" title="Domaines">
                        {selectedCircle.domains.map((domain, i) => (
                          <li key={i} className="group relative flex items-start gap-2 text-gray-700">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0"></span>
                            <span>{domain}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-400 text-sm">Aucun domaine declare.</p>
                    )}
                  </div>
                </div>

                {!isRole && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Membres</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {circleMemberIds.length > 0 ? (
                          circleMemberIds.map((id) => {
                            const emp = findEmployee(id);
                            const displayName = emp?.name?.split?.(' ')?.[0] || emp?.name || id;
                            return (
                              <div
                                key={id}
                                className="flex items-center gap-2 px-2 py-1 border border-slate-200 rounded-full bg-white shadow-sm"
                              >
                                <Avatar
                                  employee={emp}
                                  size={28}
                                  onClick={
                                    emp?.id !== undefined && emp?.id !== null
                                      ? () => onSelectEmployee && onSelectEmployee(emp.id)
                                      : undefined
                                  }
                                />
                                <div>
                                  <p className="text-sm font-semibold text-slate-800 leading-tight">{displayName}</p>
                                  <p className="text-[11px] text-slate-500 leading-tight">Membre</p>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-slate-400 text-sm">Aucun membre assigne.</p>
                        )}
                      </div>
                    </div>

                  </div>
                )}

                {!isRole && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Roles et focus</h3>
                    </div>
                    <div className="space-y-3">
                      {directRoles.length > 0 ? (
                        directRoles.map((role) => {
                          const ownerIds = new Set();
                          if (role.owner_id) ownerIds.add(role.owner_id);
                          (role.owner_ids || []).forEach((id) => ownerIds.add(id));
                          const owners = Array.from(ownerIds);
                          return (
                            <div key={role.id} className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-slate-800">{role.name}</h4>
                              </div>
                              {owners.length > 0 ? (
                                owners.map((id) => {
                                  const emp = findEmployee(id);
                                  const focusKey = `${role.id}:${id}`;
                                  const focusValue = roleFocusMap[focusKey] || '';
                                  return (
                                    <div key={id} className="flex items-start gap-3">
                                      <Avatar
                                        employee={emp}
                                        size={32}
                                        onClick={
                                          emp?.id !== undefined && emp?.id !== null
                                            ? () => onSelectEmployee && onSelectEmployee(emp.id)
                                            : undefined
                                        }
                                      />
                                      <div className="flex-1 space-y-1">
                                        <p className="text-sm font-semibold text-slate-800">{emp ? emp.name : id}</p>
                                        {canEditRoleFocus ? (
                                          <RoleFocusInput
                                            value={focusValue}
                                            onSave={(next) => onSaveRoleFocus && onSaveRoleFocus(role.id, Number(id), next)}
                                            disabled={!canEditRoleFocus}
                                            mentionItems={mentionItems}
                                            placeholder="Ajouter un focus (optionnel)"
                                            getMentionStyle={getMentionTagStyle}
                                          />
                                        ) : (
                                          <div className="text-sm text-slate-600">{renderFocusText(focusValue)}</div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="text-xs text-slate-400">Aucun membre attribue.</p>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-slate-400 text-sm">Aucun role direct.</p>
                      )}
                    </div>
                  </div>
                )}

                {!isRole && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Metrics du cercle</h3>
                      {isUsingSlotRange && (
                        <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100">
                          Slot en cours
                        </span>
                      )}
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={applyCurrentSlotRange}
                          className={slotButtonClassName}
                          disabled={slotButtonDisabled}
                        >
                          Slot en cours
                        </button>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="text-[11px] text-slate-500">
                          Debut
                          <input
                            type="date"
                            value={metricsRange?.start || ''}
                            onChange={(event) =>
                              updateMetricsRange('start', event.target.value)
                            }
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-emerald-300 focus:outline-none"
                          />
                        </label>
                        <label className="text-[11px] text-slate-500">
                          Fin
                          <input
                            type="date"
                            value={metricsRange?.end || ''}
                            onChange={(event) =>
                              updateMetricsRange('end', event.target.value)
                            }
                            className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-emerald-300 focus:outline-none"
                          />
                        </label>
                      </div>

                      {!metricsRangeValid &&
                        metricsRange?.start &&
                        metricsRange?.end && (
                          <p className="text-xs text-rose-500">
                            La date de fin doit etre apres la date de debut.
                          </p>
                        )}

                      {circleMetricsError && (
                        <p className="text-xs text-rose-500">{circleMetricsError}</p>
                      )}

                      {metricsSummary.projects === 0 ? (
                        <p className="text-xs text-slate-400">
                          Aucun projet associé pour calculer les metrics.
                        </p>
                      ) : circleMetricsLoading ? (
                        <p className="text-xs text-slate-400">
                          Chargement des metrics en cours...
                        </p>
                      ) : (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400">Temps</p>
                            <p className="text-xl font-semibold text-slate-800">
                              {formatNumber(metricsSummary.hours, 1)} h
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {metricsSummary.covered}/{metricsSummary.projects} projets suivis
                            </p>
                          </div>
                          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400">Depenses</p>
                            <p className="text-xl font-semibold text-slate-800">
                              {formatNumber(metricsSummary.expenses, 0)} €
                            </p>
                            <p className="text-[11px] text-slate-500">
                              Somme des depenses du cercle
                            </p>
                          </div>
                          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400">Velocite moyenne</p>
                            <p className="text-xl font-semibold text-slate-800">
                              {formatNumber(metricsSummary.velocity, 1)} tokens/j
                            </p>
                            <p className="text-[11px] text-slate-500">
                              Moyenne des projets sur la période
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {canViewProjects && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Projets</h3>
                    </div>
                    {inProgressProjects.length > 0 ? (
                      <div className="space-y-2">
                        {inProgressProjects.map((project) => (
                          <button
                            key={project.id}
                            type="button"
                            onClick={() => onOpenProject && onOpenProject(project.id)}
                            className="w-full text-left p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-200 hover:shadow-sm transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{project.name}</p>
                                {project.status && (
                                  <p className="text-xs text-slate-500">Statut: {project.status}</p>
                                )}
                              </div>
                              {project.project_number !== undefined && project.project_number !== null && (
                                <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                                  #{project.project_number}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm">Aucun projet en cours.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <p className="text-lg font-medium">Sélectionnez un cercle pour voir les détails</p>
          </div>
        )}
      </div>
    )}
  </>
  );
};

export default StructurePanel;
